import { NavLink } from 'react-router-dom';
import type { ComponentType, SVGProps } from 'react';
import { Logo } from './Logo';

type NavItem = {
  to: string;
  label: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
};

type Section = {
  title?: string;
  items: NavItem[];
};

export function Sidebar({ orgName }: { orgName: string }) {
  const sections: Section[] = [
    {
      items: [{ to: '/', label: 'Dashboard', Icon: IconDashboard }],
    },
    {
      title: 'Build',
      items: [
        { to: '/wallets', label: 'Wallets', Icon: IconWallet },
        { to: '/policies', label: 'Policies', Icon: IconRules },
        { to: '/api-keys', label: 'API keys', Icon: IconKey },
        { to: '/authentication', label: 'Authentication', Icon: IconLock },
        { to: '/networks', label: 'Networks', Icon: IconNetwork },
        { to: '/recovery', label: 'Recovery', Icon: IconShield },
      ],
    },
    {
      title: 'Company',
      items: [
        { to: '/audit', label: 'Audit log', Icon: IconList },
        { to: '/settings', label: 'Settings', Icon: IconCog },
      ],
    },
  ];

  return (
    <aside className="hidden w-64 shrink-0 border-r border-line-soft bg-surface-white md:block">
      <div className="flex h-14 items-center border-b border-line-soft px-5">
        <Logo variant="lockup" size={22} />
        <span className="ml-3 rounded bg-surface-soft px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-eyebrow text-ink-tertiary">
          Portal
        </span>
      </div>

      <div className="border-b border-line-soft px-5 py-4">
        <p className="font-mono text-[11px] font-medium uppercase tracking-eyebrow text-ink-tertiary">
          Company
        </p>
        <p className="mt-1.5 truncate text-sm font-medium text-ink-primary">{orgName}</p>
      </div>

      <nav className="px-3 py-4">
        {sections.map((section, i) => (
          <div key={i} className={i > 0 ? 'mt-6' : ''}>
            {section.title && (
              <p className="mb-2 px-3 font-mono text-[11px] font-medium uppercase tracking-eyebrow text-ink-tertiary">
                {section.title}
              </p>
            )}
            <ul className="space-y-0.5">
              {section.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/'}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                        isActive
                          ? 'bg-surface-soft font-medium text-ink-primary'
                          : 'text-ink-secondary hover:bg-surface-soft/60 hover:text-ink-primary'
                      }`
                    }
                  >
                    <item.Icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}

function IconDashboard(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="3" width="7" height="9" rx="1.5" />
      <rect x="14" y="3" width="7" height="5" rx="1.5" />
      <rect x="14" y="12" width="7" height="9" rx="1.5" />
      <rect x="3" y="16" width="7" height="5" rx="1.5" />
    </svg>
  );
}
function IconWallet(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="3" y="6" width="18" height="14" rx="2" />
      <path d="M3 10h18" />
      <circle cx="17" cy="15" r="1" />
    </svg>
  );
}
function IconKey(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="8" cy="14" r="4" />
      <path d="M11 14l9-9M16 7l3 3" />
    </svg>
  );
}
function IconLock(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 1 1 8 0v4" />
    </svg>
  );
}
function IconNetwork(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.6 9h16.8M3.6 15h16.8" />
      <path d="M12 3a15 15 0 0 1 4 9 15 15 0 0 1-4 9 15 15 0 0 1-4-9 15 15 0 0 1 4-9z" />
    </svg>
  );
}
function IconList(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M4 6h16M4 12h16M4 18h10" />
    </svg>
  );
}
function IconCog(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}
function IconRules(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 13h8M8 17h4" />
    </svg>
  );
}
function IconShield(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 3l8 3v5c0 4.5-3.2 8.5-8 10-4.8-1.5-8-5.5-8-10V6l8-3z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
